import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getMisTalentosService } from "../../services/talentos/talentos.service";
import { getConfirmacionStatusThunk } from "../../store/confirmacion/confirmacion.thunks";
import { getPTQuizStatusThunk } from "../../store/preguntasTalentoQuiz/preguntasTalentoQuiz.thunks";
import {
  downloadTalentosPDF,
  previewTalentosPDF,
} from "../../utils/generateTalentosPDF";
import DiplomaModal from "../../pages/test/QuizApplication/DiplomaModal";
import PdfPreviewModal from "../UI/PdfPreviewModal";
import "./talentos-collection.css";

const TalentosCollection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { phase } = useSelector((state) => state.quiz);
  const { status } = useSelector((state) => state.confirmacion);
  // Fortalezas 2.0 reemplaza los viejos quizzes de fortalezas y aplicación.
  const { status: fortalezaStatus } = useSelector(
    (state) => state.preguntasTalentoQuiz,
  );
  const { user } = useSelector((state) => state.auth);

  // Si el usuario es de registro público, no mostrar talentos no dominantes
  const esRegistroPublico = user?.registro_publico === true;

  // Fase 1 completada: la evaluación está terminada
  const evaluacionCompletada = phase === "completada";
  // Fase 2 completada: todas las confirmaciones están listas
  const confirmacionCompletada = status?.todas_completadas === true;
  // Fase 3 completada: quiz Fortalezas 2.0 terminado (último paso del flujo).
  const fortalezaCompletada = fortalezaStatus?.todas_completadas === true;

  const [talentos, setTalentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const [certOpen, setCertOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const todosLosQuizCompletados =
    evaluacionCompletada && confirmacionCompletada && fortalezaCompletada;

  const talentosParaCertificado = useMemo(
    () =>
      (talentos?.talentos_optimos || [])
        .slice()
        .sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99))
        .map((t) => ({
          ranking: t.ranking,
          nombre: t.talento_nombre || "",
        })),
    [talentos],
  );

  useEffect(() => {
    const fetchTalentos = async () => {
      try {
        const data = await getMisTalentosService();
        setTalentos(data);
      } catch (error) {
        // Error al cargar talentos
      } finally {
        setLoading(false);
      }
    };

    fetchTalentos();
  }, []);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        await dispatch(getConfirmacionStatusThunk()).unwrap();
      } catch (error) {
        // Las confirmaciones aún no están disponibles
      }
    };

    // Solo cargar estado de confirmación si la evaluación ya fue completada
    if (evaluacionCompletada) {
      loadStatus();
    }
  }, [dispatch, evaluacionCompletada]);

  useEffect(() => {
    // Solo cargar estado de Fortalezas 2.0 si la confirmación ya fue completada
    if (confirmacionCompletada) {
      dispatch(getPTQuizStatusThunk());
    }
  }, [dispatch, confirmacionCompletada]);

  const toggleExpand = (talentoId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [talentoId]: !prev[talentoId],
    }));
  };

  const shouldShowMoreButton = (description) => {
    return description && description.length > 250;
  };

  if (loading) {
    return (
      <div className="talentos-loading">
        <div className="spinner"></div>
        <p>Cargando tus talentos...</p>
      </div>
    );
  }

  const talentosOptimos = talentos?.talentos_optimos || [];
  const talentosAMejorar = talentos?.talentos_a_mejorar || [];

  return (
    <div className="talentos-page">
      <div className="talentos-container">
        {/* Bloque talentos óptimos */}
        <section className="talentos-section talentos-section--optimos">
          <header className="talentos-page-header">
            {/* ── Phase Stepper Tabs ── */}
            <nav className="phase-stepper">
              {/* Paso 1: Identificar */}
              <button
                className={`phase-step ${
                  evaluacionCompletada
                    ? "phase-step--completed"
                    : "phase-step--active"
                }`}
                onClick={() =>
                  !evaluacionCompletada && navigate("/quiz")
                }
                type="button"
              >
                <span className="phase-step__icon">
                  <Icon
                    icon={
                      evaluacionCompletada
                        ? "mdi:check-bold"
                        : "mdi:magnify"
                    }
                  />
                </span>
                <span className="phase-step__label">
                  Identificar
                  <span className="phase-step__sublabel">
                    {evaluacionCompletada ? "Completado" : "En progreso"}
                  </span>
                </span>
              </button>

              <div
                className={`phase-connector ${
                  evaluacionCompletada ? "phase-connector--done" : ""
                }`}
              />

              {/* Paso 2: Confirmar */}
              <button
                className={`phase-step ${
                  !evaluacionCompletada
                    ? "phase-step--locked"
                    : confirmacionCompletada
                      ? "phase-step--completed"
                      : "phase-step--active"
                }`}
                onClick={() =>
                  evaluacionCompletada && navigate("/confirmacion-talentos")
                }
                type="button"
              >
                <span className="phase-step__icon">
                  <Icon
                    icon={
                      confirmacionCompletada
                        ? "mdi:check-bold"
                        : evaluacionCompletada
                          ? "mdi:star-check"
                          : "mdi:lock-outline"
                    }
                  />
                </span>
                <span className="phase-step__label">
                  Tus fortalezas 1 - 4
                  <span className="phase-step__sublabel">
                    {!evaluacionCompletada
                      ? "Bloqueado"
                      : confirmacionCompletada
                        ? "Completado"
                        : status?.talento_nombre
                          ? "En progreso"
                          : "Pendiente"}
                  </span>
                </span>
              </button>

              <div
                className={`phase-connector ${
                  confirmacionCompletada ? "phase-connector--done" : ""
                }`}
              />

              {/* Paso 3: Fortalezas */}
              <button
                className={`phase-step ${
                  !confirmacionCompletada
                    ? "phase-step--locked"
                    : fortalezaCompletada
                      ? "phase-step--completed"
                      : "phase-step--active"
                }`}
                onClick={() =>
                  confirmacionCompletada && navigate("/fortalezas-quiz")
                }
                type="button"
              >
                <span className="phase-step__icon">
                  <Icon
                    icon={
                      fortalezaCompletada
                        ? "mdi:check-bold"
                        : confirmacionCompletada
                          ? "mdi:shield-star"
                          : "mdi:lock-outline"
                    }
                  />
                </span>
                <span className="phase-step__label">
                Tus fortalezas 5 - 7
                  <span className="phase-step__sublabel">
                    {!confirmacionCompletada
                      ? "Bloqueado"
                      : fortalezaCompletada
                        ? "Completado"
                        : fortalezaStatus?.talento_nombre
                          ? "En progreso"
                          : "Pendiente"}
                  </span>
                </span>
              </button>

              {/* Paso "Aplicar" oculto — reemplazado por Fortalezas 2.0.
                  Mantener el código por si se restablece. */}
            </nav>

            <h1 className="page-title page-title--celebracion">
              <span className="page-title__saludo">¡Felicidades!</span>
              <span className="page-title__mensaje">
                Estos son tus <strong>{talentosOptimos.length}</strong> talentos más poderosos
              </span>
            </h1>

           

            {/* ── Botones de descarga ── */}
            {talentosOptimos.length > 0 && (
              <div className="talentos-actions">
                <button
                  className="talentos-download-btn"
                  onClick={() => setPdfOpen(true)}
                  type="button"
                >
                  <Icon icon="mdi:file-pdf-box" />
                  <span>Descargar PDF</span>
                  <Icon icon="mdi:download" />
                </button>

                {todosLosQuizCompletados && (
                  <button
                    className="talentos-certificado-btn"
                    onClick={() => setCertOpen(true)}
                    type="button"
                  >
                    <Icon icon="mdi:certificate" />
                    <span>Ver mi certificado</span>
                  </button>
                )}
              </div>
            )}
          </header>

          <DiplomaModal
            open={certOpen}
            onClose={() => setCertOpen(false)}
            nombre={user?.nombre_completo}
            talentos={talentosParaCertificado}
          />

          <PdfPreviewModal
            open={pdfOpen}
            onClose={() => setPdfOpen(false)}
            title="Vista previa · Mis Talentos"
            previewFn={previewTalentosPDF}
            downloadFn={downloadTalentosPDF}
            previewArgs={{ talentos, esRegistroPublico }}
          />

          <section className="talentos-summary">
            <div className="summary-pills">
              {talentosOptimos.map((talento, index) => (
                <div
                  key={talento.talento_id}
                  className="talent-pill talent-pill--optimo"
                >
                  <span className="pill-rank">
                    #{talento.ranking ?? index + 1}
                  </span>
                  <span className="pill-name">{talento.talento_nombre}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="talentos-cards-section">
            {talentosOptimos.map((talento) => {
              const isExpanded = expandedCards[talento.talento_id];
              const showButton = shouldShowMoreButton(
                talento.talento_descripcion,
              );

              return (
                <article
                  key={talento.talento_id}
                  className="talent-card talent-card--optimo"
                >
                  <div className="card-header">
                    <p className="card-chip card-chip--optimo">
                      Talento fuerte
                    </p>
                    <h4 className="card-title">
                      <span className="card-rank">#{talento.ranking}</span>{" "}
                      {talento.talento_nombre}
                    </h4>
                  </div>

                  <div
                    className={`card-description ${!isExpanded && showButton ? "truncated" : ""}`}
                  >
                    {talento.talento_descripcion}
                  </div>

                  {showButton && (
                    <div className="card-actions">
                      <button
                        className="expand-btn"
                        onClick={() => toggleExpand(talento.talento_id)}
                      >
                        {isExpanded ? "Ver menos" : "Ver más"}
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </section>

          {/* CTA: continuar al quiz en el que se encuentra el usuario */}
          {evaluacionCompletada && !todosLosQuizCompletados && (
            <div className="talentos-continuar-wrap">
              <button
                type="button"
                className="talentos-continuar-btn"
                onClick={() => {
                  if (!confirmacionCompletada) {
                    navigate("/confirmacion-talentos");
                  } else {
                    navigate("/fortalezas-quiz");
                  }
                }}
              >
                <Icon icon="mdi:arrow-right-circle" />
                <span>Sigamos construyendo tus 7 fortalezas</span>
              </button>
            </div>
          )}
        </section>

        {/* Bloque talentos a mejorar — oculto para registro público */}
        {talentosAMejorar.length > 0 && !esRegistroPublico && (
          <section className="talentos-section talentos-section--mejorar">
            <header className="talentos-page-header talentos-page-header--mejorar">
              <h2 className="page-title page-title--celebracion page-title--mejorar">
                <span className="page-title__mensaje">Talentos no dominantes</span>
              </h2>
             
            </header>

            <section className="talentos-summary">
              <div className="summary-pills">
                {talentosAMejorar.map((talento, index) => (
                  <div
                    key={talento.talento_id}
                    className="talent-pill talent-pill--mejorar"
                  >
                    <span className="pill-rank">
                      #{talento.ranking ?? index + 1}
                    </span>
                    <span className="pill-name">{talento.talento_nombre}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="talentos-cards-section">
              {talentosAMejorar.map((talento) => {
                const isExpanded = expandedCards[talento.talento_id];
                const showButton = shouldShowMoreButton(
                  talento.talento_descripcion,
                );

                return (
                  <article
                    key={talento.talento_id}
                    className="talent-card talent-card--mejorar"
                  >
                    <div className="card-header">
                      <p className="card-chip card-chip--mejorar">
                        Talento por desarrollar
                      </p>
                      <h4 className="card-title">
                        <span className="card-rank">#{talento.ranking}</span>{" "}
                        {talento.talento_nombre}
                      </h4>
                    </div>

                    <div
                      className={`card-description ${!isExpanded && showButton ? "truncated" : ""}`}
                    >
                      {talento.talento_descripcion}
                    </div>

                    {showButton && (
                      <div className="card-actions">
                        <button
                          className="expand-btn expand-btn--mejorar"
                          onClick={() => toggleExpand(talento.talento_id)}
                        >
                          {isExpanded ? "Ver menos" : "Ver más"}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
          </section>
        )}
      </div>
    </div>
  );
};

export default TalentosCollection;
